/**
 * Filet global. Une erreur de rendu ne doit pas laisser un ecran blanc : le
 * jeu est temps reel, un joueur bloque penalise toute la table.
 *
 * Classe et non hook : React n'expose pas componentDidCatch autrement.
 */
import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { translate, type Language } from '../locales/index.js';
import { useUiStore } from '../store/uiStore.js';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Pas de telemetrie : la console suffit tant qu'il n'y a pas de collecte
    // declaree (aucune donnee n'est persistee, cf. CLAUDE.md).
    console.error('[boundary] rendu interrompu', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    // Le store est lu directement : un hook est impossible dans une classe, et
    // l'ecran d'erreur ne se re-rend pas.
    return <ErrorScreen language={useUiStore.getState().language} />;
  }
}

function ErrorScreen({ language }: { language: Language }): ReactElement {
  return (
    <main className="boundary" role="alert">
      <h1 className="boundary__title">{translate(language, 'boundary.title')}</h1>
      <p className="boundary__body">{translate(language, 'boundary.body')}</p>
      <button
        type="button"
        className="button-primary"
        onClick={() => window.location.reload()}
      >
        {translate(language, 'boundary.reload')}
      </button>
    </main>
  );
}
